import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES, changeLanguage } from '@/i18n';
import styles from './LanguageSelectorModal.module.css';

interface LanguageSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LanguageSelectorModal: React.FC<LanguageSelectorModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();

  const handleSelect = useCallback((code: string) => {
    changeLanguage(code);
    onClose();
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('common.languageSelection', 'Language Selection')}</h2>
        </div>
        <div className={styles.list}>
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isActive = i18n.language?.startsWith(lang.code);
            return (
              <button
                key={lang.code}
                type="button"
                className={`${styles.option} ${isActive ? styles.optionActive : ''}`}
                onClick={() => handleSelect(lang.code)}
              >
                <span className={styles.optionLabel}>{lang.nativeName}</span>
                {isActive && <Check size={20} strokeWidth={2.5} className={styles.checkmark} />}
              </button>
            );
          })}
        </div>
        <div className={styles.cancelWrapper}>
          <button type="button" className={styles.cancelButton} onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};
